// kfe-l2b-exercise.js — Language-to-Body exercise screen.
//
// PORT TARGET: Assets/KFE/Scripts/Runtime/Core/UI/Exercise/L2B/L2BExerciseScreen.cs
//
// Consumes L2BEngine via state snapshots only (architecture rule). Manages:
//   • the large-intro panel (full-screen image + replay/advance)
//   • the tile grid + target audio
//   • brief feedback + advance to the next round
//   • progress reporting to the learner store: per-exercise L2B mastery streaks
//     (l2bStreaks, scoped [itemId, mmuId] — Brief 182) drive the already-mastered
//     pool filter, while the global mmuStreaks row is still written so trained
//     words stay "met" for L2B Testing.
//
// Audio rules:
//   • play(target) is fired off the user-gesture-initiated start; subsequent
//     plays inherit Safari's unlocked AudioContext (CLAUDE.md pitfall #3).
//   • A `_lastAudioTarget` guard mirrors the Unity port's HAAI bug fix —
//     don't replay if the target hasn't changed (e.g. feedback frames).

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { persistCompletion } from "./completion-persist.js";
import {
  L2BEngine,
  applyL2bDefensiveFilters,
  mmusToItemData,
} from "../engines/l2b-engine.js";
import { l2bModeRenderFlags } from "../data/mmu-loader.js";
import { resolveImage } from "../data/media-paths.js";

/** @param {string|null} l2bType → L2BConfig.modality */
function modalityFor(l2bType) {
  if (l2bType === "Text only") return "textOnly";
  if (l2bType === "Audio and Text") return "audioAndText";
  return "audioOnly";
}

const FEEDBACK_MS = 900;

/**
 * Decode an image URL into the browser cache so the tile paints on the first
 * frame after the atomic swap (Brief 134 — eliminates the first-run blank-tile
 * window). Resolves even on error/timeout so one missing or slow file never
 * blocks the whole grid from appearing.
 * @param {string} url
 * @returns {Promise<void>}
 */
function preloadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    const done = () => resolve();
    img.onload = done;
    img.onerror = done;
    img.src = url;
  });
}

export class KFEL2BExercise extends KFEComponent {
  static props = ["itemId"];
  static styles = `
    /* Brief 135b: bound the exercise to an iPhone-canon-proportioned column and
       centre it. Without this the tile grid (4× 1fr columns) widened to the full
       viewport on desktop / browser-zoom while the drop target stayed pinned to
       a viewport-relative size, so the two drifted out of proportion. With a
       shared bounded container BOTH the grid and the now container-relative
       target zone (see kfe-l2b-drag-area .target-zone) scale in lockstep. */
    :host { display: block; max-width: min(720px, 100vw - 32px); margin-inline: auto; }
    h2 { margin-top: 0; }
    .target { margin: 1rem 0; padding: 0.75rem; border-radius: 8px;
              background: var(--kfe-surface); display: flex;
              align-items: center; gap: 1rem; }
    .target .word { font-size: var(--kfe-h2); flex: 1; }
    .grid { margin-top: 1rem; }
    .complete { padding: 2rem; text-align: center; color: var(--kfe-muted); }
    button.primary { padding: 0.5rem 1rem; border-radius: 8px;
                     background: var(--kfe-accent); color: var(--kfe-on-accent);
                     border: 0; cursor: pointer; }
    button.replay { padding: 0.5rem 1rem; border-radius: 8px;
                    border: 1px solid var(--kfe-border); background: transparent;
                    color: var(--kfe-fg); cursor: pointer; }
    .large-intro { display: flex; flex-direction: column; align-items: center;
                   gap: 1rem; padding: 1.5rem 0; }
    /* White background — same rationale as kfe-tile-grid: L2B images are
       transparent-PNG line drawings designed for white-paper rendering. On
       the dark theme surface they're invisible; the shared --kfe-l2b-image-bg
       token restores the canonical iOS white image cell (Brief 061). Keeps
       its larger 12px radius / 1rem padding for the full-screen intro panel. */
    .large-intro img.l2b-image-container {
                       max-width: 80vw; max-height: 50vh; object-fit: contain;
                       border-radius: 12px; background: var(--kfe-l2b-image-bg);
                       padding: 1rem; box-sizing: border-box; }
    .large-intro .word { font-size: var(--kfe-h1); text-align: center; }
    .start-prompt { padding: 2rem; text-align: center; }
    /* Progress row: thin accent-coloured bar + numeric label, mirroring
       the iOS KFEProgressLine + Unity port's progressBar/progressLabel
       pair. Bar fills with --kfe-accent; the empty rail sits on
       --kfe-surface so it reads against the bg. Width animates so
       streak-mastery transitions visibly tick the bar forward. */
    .progress-row { display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 0.75rem; }
    .progress-bar { flex: 1; height: 10px; background: var(--kfe-surface);
                    border-radius: 5px; overflow: hidden; }
    .progress-fill { height: 100%; background: var(--kfe-accent);
                     border-radius: 5px;
                     transition: width 0.3s ease-out; }
    .progress { font-size: 1.05rem; font-weight: 600; color: var(--kfe-fg);
                white-space: nowrap; }
    /* Feedback shown briefly after a tap. Big and prominent to match
       iOS canon and the L2B Test screen. */
    .feedback { padding: 1rem 1.5rem; border-radius: 12px; margin: 0.75rem 0;
                font-weight: 700; font-size: 2.5rem; text-align: center;
                line-height: 1; }
    .feedback.correct { background: #1f3d1f; color: #4caf50;
                        border: 2px solid #4caf50; }
    .feedback.wrong   { background: #3d1f1f; color: #e57373;
                        border: 2px solid #e57373; }
  `;

  /** @type {L2BEngine | null} */
  engine = null;

  constructor() {
    super();
    this._started = false;
    /** @type {string|null} */ this._lastAudioConceptId = null;
    /** @type {string|null} */ this._lastMode = null;
    /** @type {string|null} */ this._largeIntroImageUrl = null;
    /** @type {ReturnType<typeof setTimeout>|null} */ this._feedbackTimer = null;
    /** @type {number[]} */ this._allItemMmuIds = [];
    this._allMasteredAtStart = false;
    this._completionPersisted = false;
    // Brief 199: a completion write is in flight (so a second terminal event
    // in this lifecycle does not start another one).
    this._completionPending = false;
    // Brief 101: per-mode rendering flags (Audio only hides text + auto-plays;
    // Text only shows text + no auto-play; Audio and Text both). Defaults to the
    // pre-101 behaviour until onConnected reads the item's L2BType.
    /** @type {string|null} */ this._l2bType = null;
    this._renderFlags = { showText: true, autoPlay: true };
    // Brief 134: atomic tile renewal. `_tileUrls` is the pre-resolved
    // { mmuId → imageUrl } map handed to <kfe-l2b-drag-area> so the grid paints
    // images on its first frame (no JIT-resolution blank window). `_prepareToken`
    // guards against a stale pre-resolution finishing after a newer state (fast
    // taps / redo) has superseded it.
    /** @type {Record<number,string>} */ this._tileUrls = {};
    this._prepareToken = 0;
  }

  async onConnected() {
    await services.mmus.loadMmuRegistry();
    const itemId = Number(/** @type {any} */ (this).itemId);

    // Brief 101: read the item's L2B mode for per-mode stimulus rendering.
    this._l2bType = services.mmus.getL2bType(itemId);
    this._renderFlags = l2bModeRenderFlags(this._l2bType);

    let mmus = services.mmus.getMmusForItem(itemId);
    mmus = applyL2bDefensiveFilters(mmus);

    // Persistence: words the learner already mastered IN THIS EXERCISE shouldn't
    // reappear in the active pool on re-entry. The engine's runtime cache is
    // separate; we filter pre-pool so already-mastered words skip even the intro.
    //
    // Brief 182: this is scoped per-exercise (getMasteredL2bMmuIdsForItem), NOT
    // global (the old getMasteredMmuIds). Stage 2's L2B lessons share MMUs —
    // Lesson 4's item holds 10 of Lesson 6's 13 — so a global filter stripped the
    // 10 a learner mastered in Lesson 4 from Lesson 6, leaving 3 of 13 tiles.
    // iOS scopes L2B mastery per exerciseId (KFELanguageBodyViewModel.m:637).
    const masteredIds = new Set(await services.learner.getMasteredL2bMmuIdsForItem(itemId));
    const filtered = mmus.filter((m) => !masteredIds.has(m.mmuId));
    // Remember the full MMU set for Redo (Brief 046): a Redo clears every
    // streak entry for the item so the next walk starts at zero — even MMUs
    // that were already mastered before this session began.
    this._allItemMmuIds = mmus.map((m) => m.mmuId);
    if (filtered.length === 0 && mmus.length > 0) {
      // All items were already mastered — show the complete state immediately.
      this._allMasteredAtStart = true;
      this._markCompleted();
      this._scheduleRender();
      return;
    }

    const itemData = mmusToItemData(filtered);

    this.engine = new L2BEngine(itemData, {
      defaultMasteryStreak: 3,
      modality: modalityFor(this._l2bType),
    });

    const onState = () => {
      const snap = this.engine?.snapshot();
      if (!snap) return;
      // Brief 060b: persist completion once the engine reaches "complete" so
      // this L2B item's MMU words enter the SR Auto met-pool.
      if (snap.mode === "complete") this._markCompleted();
      // Brief 134: hold the current frame until the NEXT question's tiles are
      // fully resolved + decoded, then repaint target + grid in lockstep. Without
      // the gate, the fresh <kfe-l2b-drag-area> resolves URLs (and downloads
      // bytes) JIT, leaving a blank-tile window that lets the learner wait for the
      // new tile to appear and pick it. iOS swaps all 8 tiles + the target box
      // atomically (KFELanguageBodyViewModel.m:381-422). The feedback frame
      // reuses the prior round's already-preloaded tiles, so it renders at once.
      if (snap.mode === "tileGrid" && snap.tiles.length) {
        this._prepareTilesThenRender(snap);
      } else {
        this._scheduleRender();
      }
    };
    const onResult = (/** @type {Event} */ e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      const mmuId = Number(detail.conceptId);
      if (Number.isFinite(mmuId)) {
        // Brief 182: per-exercise streak drives the already-mastered pool filter
        // on re-entry (scoped so a shared word's mastery doesn't leak across
        // lessons). The masteryTarget matches the engine's default streak (3).
        services.learner.recordL2bMmuAttempt(itemId, mmuId, !!detail.correct, 3).catch((err) => {
          console.warn("[l2b] recordL2bMmuAttempt failed", err);
        });
        // Still write the global streak so this word stays "met" for L2B Testing
        // (iOS L2B calls learnerMetMmuWithMmuID globally — KFELanguageBodyViewModel.m:186).
        services.learner.recordMmuAttempt(mmuId, !!detail.correct, 3).catch((err) => {
          console.warn("[l2b] recordMmuAttempt failed", err);
        });
      }
    };

    this.engine.addEventListener("stateChanged", onState);
    this.engine.addEventListener("result", onResult);
    this.addCleanup(() => this.engine?.removeEventListener("stateChanged", onState));
    this.addCleanup(() => this.engine?.removeEventListener("result", onResult));

    // Don't auto-start: Safari's audio gesture rule means the first play()
    // must be on a user click. Render a Start button; user click drives
    // engine.start() → first questionChanged → first audio play.
    this._scheduleRender();
  }

  onDisconnected() {
    if (this._feedbackTimer) clearTimeout(this._feedbackTimer);
    services.audio.stop();
  }

  /** Persist item completion once per view (Brief 060b).
  // Brief 199, criterion 3: the latch is set only AFTER the write has resolved,
  // and a failure leaves it clear so a later terminal event can try again. It
  // used to be set before the write was even attempted, so a rejected write was
  // never retried by this view — six of the 2022 walk's nine lost completions.
  // `_completionPending` keeps a second terminal event in the same lifecycle
  // from starting a second write (learner-store shares the in-flight promise
  // too, so neither can double-count — `completedCount` is a counter, Brief 072).
   */
  async _markCompleted() {
    if (this._completionPersisted || this._completionPending) return;
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(itemId)) return;
    this._completionPending = true;
    const ok = await persistCompletion(itemId);
    this._completionPending = false;
    this._completionPersisted = ok;
  }

  _handleStart() {
    if (!this.engine || this._started) return;
    this._started = true;
    const state = this.engine.start();
    this._afterStateChange(state);
  }

  _handleAdvance() {
    if (!this.engine) return;
    const state = this.engine.onAdvancePressed();
    this._afterStateChange(state);
  }

  _handleReplay() {
    if (!this.engine) return;
    const state = this.engine.snapshot();
    const target = state.targetItem ?? state.introItem;
    if (target) this._playAudioForTarget(target, /*force*/ true);
  }

  /** @param {string} conceptId */
  _handlePick(conceptId) {
    if (!this.engine) return;
    if (this._feedbackTimer) return; // ignore double-taps during feedback
    const fb = this.engine.onTileSelected(conceptId);
    this._afterStateChange(fb);
    this._feedbackTimer = setTimeout(() => {
      this._feedbackTimer = null;
      const next = this.engine?.onFeedbackComplete();
      if (next) this._afterStateChange(next);
    }, FEEDBACK_MS);
  }

  /** @param {import("../engines/l2b-engine.js").L2BState} state */
  _afterStateChange(state) {
    const prevMode = this._lastMode;
    this._lastMode = state.mode;

    // Brief 101: Text-only mode does NOT auto-play audio on present (the
    // stimulus is the visible phrase); the replay button still plays on demand.
    const autoPlay = this._renderFlags.autoPlay;
    if (state.mode === "largeIntro" && state.introItem) {
      if (autoPlay) this._playAudioForTarget(state.introItem, false);
      this._resolveLargeIntroImage(state.introItem);
    } else if (state.mode === "tileGrid" && state.targetItem) {
      // Replay whenever we ENTER tileGrid from anywhere else — largeIntro
      // → grid (the just-introduced item is now the prompt), or feedback
      // → next round (even when the same target repeats in a single-active
      // edge case). The conceptId guard inside _playAudioForTarget only
      // matters for the defensive case of two back-to-back tileGrid states
      // with the same target.
      if (autoPlay) {
        const force = prevMode !== "tileGrid";
        this._playAudioForTarget(state.targetItem, force);
      }
    }
    // No need to scheduleRender — stateChanged listener already did.
  }

  /**
   * Brief 134: pre-resolve + decode every tile image for `state` BEFORE swapping
   * the visible grid, so the target prompt and all tiles repaint together. While
   * this awaits, the screen keeps showing the previous frame (the feedback ✓/✗
   * over the old grid) — that hold IS the atomic swap. The `_prepareToken` guard
   * drops the result if a newer state superseded this one mid-resolve.
   * @param {import("../engines/l2b-engine.js").L2BState} state
   */
  async _prepareTilesThenRender(state) {
    const tiles = state.tiles ?? [];
    const token = ++this._prepareToken;
    /** @type {Record<number,string>} */
    const urls = {};
    await Promise.all(
      tiles.map(async (d) => {
        const url = await resolveImage(d.mmu);
        if (!url) return;
        urls[d.mmu.mmuId] = url;
        await preloadImage(url);
      }),
    );
    // A newer transition (fast tap, redo, unmount) won the race — discard.
    if (token !== this._prepareToken || !this._connected) return;
    this._tileUrls = urls;
    this._scheduleRender();
  }

  /**
   * @param {import("../engines/l2b-engine.js").L2BItemData} target
   * @param {boolean} force
   */
  _playAudioForTarget(target, force) {
    if (!force && this._lastAudioConceptId === target.conceptId) return;
    this._lastAudioConceptId = target.conceptId;
    services.audio.play(target.mmu).catch((e) => {
      console.warn("[l2b] audio play failed for", target.conceptId, e);
    });
  }

  // Brief 046 completion-screen handlers. Redo must clear the persisted
  // streaks BEFORE re-init so the engine reads the cleared state when it
  // calls getMasteredL2bMmuIdsForItem in onConnected (Gotchas §2). Return-to-lesson
  // walks one step back in pushState history, which the router handles
  // via its popstate listener.
  async _onRedo() {
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (Number.isFinite(itemId)) {
      try {
        // Brief 182: also drop the per-exercise L2B streaks (the new pool-filter
        // source of truth), symmetric with the SR screen's redo path. The global
        // clearItemProgress still clears the item row + global streaks + tick.
        await services.learner.clearL2bMmuStreaksForItem(itemId, this._allItemMmuIds);
        await services.learner.clearItemProgress(itemId, this._allItemMmuIds);
      } catch (err) {
        console.warn("[l2b] clearItemProgress failed", err);
      }
    }
    // Tear down the existing engine + flags and re-run the connect path so
    // the freshly-cleared store is read from scratch.
    if (this._feedbackTimer) { clearTimeout(this._feedbackTimer); this._feedbackTimer = null; }
    services.audio.stop();
    this._started = false;
    this._allMasteredAtStart = false;
    this._lastAudioConceptId = null;
    this._lastMode = null;
    this._largeIntroImageUrl = null;
    // Brief 134: invalidate any in-flight tile pre-resolution and clear the
    // map so the rebuilt engine's first grid resolves from scratch.
    this._prepareToken++;
    this._tileUrls = {};
    this.engine = null;
    this._scheduleRender();
    this.onConnected();
  }

  _onReturnToLesson() {
    history.back();
  }

  /** @param {string} message */
  _renderCompletion(message) {
    // Brief 049: iOS-canonical L2B Training summary (KFELTBSummaryView) stacks
    // two buttons vertically — "Practice some more" (继续练习) above "Go back to
    // the menu" (返回课程列表). "Practice some more" restarts training (redo);
    // "Go back to the menu" returns to the lesson.
    const screen = document.createElement("kfe-completion-screen");
    /** @type {any} */ (screen).message = message;
    /** @type {any} */ (screen).layout = "column";
    /** @type {any} */ (screen).actions = [
      { label: t("completion.l2bTraining.practiceMore"), eventName: "kfe:l2bTraining:practiceMore", primary: true },
      { label: t("completion.l2bTraining.backToMenu"), eventName: "kfe:l2bTraining:backToMenu" },
    ];
    screen.addEventListener("kfe:l2bTraining:practiceMore", () => this._onRedo());
    screen.addEventListener("kfe:l2bTraining:backToMenu", () => this._onReturnToLesson());
    return html`${screen}`;
  }

  /** @param {import("../engines/l2b-engine.js").L2BItemData} target */
  async _resolveLargeIntroImage(target) {
    this._largeIntroImageUrl = null;
    const url = await resolveImage(target.mmu);
    if (this._lastAudioConceptId === target.conceptId) {
      this._largeIntroImageUrl = url ?? null;
      this._scheduleRender();
    }
  }

  render() {
    if (this._allMasteredAtStart) {
      return this._renderCompletion(t("l2b.complete") + " — " + t("l2b.completedCount", { n: "all" }));
    }
    if (!this.engine) return html`<p>${t("loading")}</p>`;

    if (!this._started) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "primary";
      btn.textContent = t("lesson.start");
      btn.addEventListener("click", () => this._handleStart());
      return html`
        <h2>${t("l2b.title")}</h2>
        <div class="start-prompt">${btn}</div>
      `;
    }

    const s = this.engine.snapshot();
    const pct = s.totalItems > 0
      ? Math.round((s.masteredCount / s.totalItems) * 100)
      : 0;
    // Progress row reused across largeIntro and tileGrid render branches.
    // Mirrors iOS KFEProgressLine + Unity port progressBar/progressLabel.
    const progressRow = html`
      <div class="progress-row" role="progressbar"
           aria-valuemin="0" aria-valuemax="${s.totalItems}"
           aria-valuenow="${s.masteredCount}">
        <div class="progress-bar"><div class="progress-fill" style="width: ${pct}%"></div></div>
        <div class="progress">${s.masteredCount} / ${s.totalItems} mastered</div>
      </div>
    `;

    if (s.mode === "complete") {
      return this._renderCompletion(
        t("l2b.complete") + " — " + t("l2b.completedCount", { n: String(s.masteredCount) }),
      );
    }

    if (s.mode === "largeIntro" && s.introItem) {
      // Brief 136: largeIntro is see-and-hear-only — large image + audio
      // controls + progress + label. It deliberately does NOT render
      // <kfe-l2b-drag-area>; the drop zone ("Drag the matching picture here")
      // lives only in that component and must stay hidden until the tileGrid
      // phase. Mirrors iOS canon (KFELTBigImageInfoView.m:28-35) where the
      // fullscreen big image occludes KFETargetsView during the intro, and
      // Brief 134's atomic swap reveals tiles + drop zone together. Do NOT add
      // the drag-area to this branch.
      const advance = document.createElement("button");
      advance.type = "button";
      advance.className = "primary";
      advance.textContent = "▶";
      advance.addEventListener("click", () => this._handleAdvance());

      const replay = document.createElement("button");
      replay.type = "button";
      replay.className = "replay";
      replay.textContent = t("audio.replay");
      replay.addEventListener("click", () => this._handleReplay());

      const img = this._largeIntroImageUrl
        ? Object.assign(document.createElement("img"), {
            src: this._largeIntroImageUrl,
            alt: s.introItem.displayText,
            className: "l2b-image-container",
          })
        : document.createTextNode("");

      return html`
        ${progressRow}
        <h2>${t("l2b.title")}</h2>
        <div class="large-intro">
          ${img}
          ${this._renderFlags.showText
            ? html`<div class="word">${s.introItem.displayText}</div>`
            : html``}
          <div>${replay} ${advance}</div>
        </div>
      `;
    }

    // tileGrid or feedback
    const target = s.targetItem;
    if (!target) return html`<p>${t("loading")}</p>`;

    // L2B Training uses drag-and-drop (per iOS canon: KFEContentsView pan
    // gesture → KFETargetsView drop). L2B Test uses the same kfe-l2b-drag-area
    // gesture — iOS/Unity reuse the same drag layer for both modes.
    const dragArea = document.createElement("kfe-l2b-drag-area");
    /** @type {any} */ (dragArea).items = s.tiles.map((d) => d.mmu);
    // Brief 134: hand the pre-resolved tile URLs to the drag-area so it paints
    // images on its first frame rather than resolving them JIT (blank window).
    /** @type {any} */ (dragArea).urls = this._tileUrls;
    /** @type {any} */ (dragArea).disabled = s.mode === "feedback";
    dragArea.addEventListener("pick", (e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      this._handlePick(String(detail.mmuId));
    });

    const replay = document.createElement("button");
    replay.type = "button";
    replay.className = "replay";
    replay.textContent = t("audio.replay");
    replay.addEventListener("click", () => this._handleReplay());

    const fbBlock = s.mode === "feedback"
      ? html`<div class="feedback ${s.wasCorrect ? "correct" : "wrong"}">
          ${s.wasCorrect ? "✓" : "✗"}
        </div>`
      : html``;

    return html`
      ${progressRow}
      <h2>${t("l2b.title")}</h2>
      <div class="target">
        ${this._renderFlags.showText
          ? html`<span class="word">${target.displayText}</span>`
          : html`<span class="word" aria-hidden="true"></span>`}
        ${replay}
      </div>
      ${fbBlock}
      <div class="grid">${dragArea}</div>
    `;
  }
}
